'use client'

import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { User } from '@supabase/supabase-js'
import { z } from 'zod'
import { createBrowserAuthClient } from '@/lib/supabase/browserAuth'
import { setUserContext, trackAuthSuccess } from '@/lib/monitoring/sentry'
import { trackLogin } from '@/lib/monitoring/analytics'
import { clearDashboardSnapshotCacheForUser } from '@/lib/dashboard/readiness/cache'
import { refreshProfileForIdentity, resolveAuthTransition } from '@/lib/auth/authTransition'
import { logAuthError } from '@/lib/auth/errors'
import {
  clearLegacyTeamCreationDrafts,
  clearTeamCreationDraftsForUser
} from '@/lib/utils/teamCreationDraftStorage'

const authProfileResponseSchema = z
  .object({
    success: z.literal(true),
    data: z.object({
      profile: z
        .object({
          id: z.string().uuid(),
          username: z.string().nullable(),
          display_name: z.string().nullable(),
          photo_url: z.string().nullable(),
          referral_code: z.string().nullable(),
          privacy_settings: z.record(z.string(), z.unknown()).nullable(),
          notification_preferences: z.record(z.string(), z.unknown()).nullable(),
          country: z.string().nullable(),
          date_of_birth: z.string().nullable(),
          region: z.string().nullable(),
          username_chosen: z.boolean(),
          phone_verified: z.boolean(),
          onboarding_completed: z.boolean(),
          prize_eligible: z.boolean(),
          onboarding_step: z.string(),
          terms_accepted_at: z.string().nullable(),
          account_status: z.string().nullable(),
          age_verified: z.boolean(),
          kyc_verified: z.boolean(),
          kyc_review_status: z.string().nullable(),
          kyc_status: z.string().nullable()
        })
        .strict()
    })
  })
  .strict()

type Profile = z.infer<typeof authProfileResponseSchema>['data']['profile']

/**
 * Client-side auth state. Used by UI components to read the current user and
 * profile. Post-auth *navigation* is handled server-side in layout guards
 * (`lib/auth/serverAuth.ts`) — this provider should never redirect on
 * sign-in; it only exposes state.
 */
interface AuthState {
  user: User | null
  profile: Profile | null
  isLoading: boolean
  isPhoneVerified: boolean
  isAgeVerified: boolean
  hasCountry: boolean
  isOnboardingComplete: boolean
  error: string | null
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

const initialState: Omit<AuthState, 'signOut' | 'refreshProfile'> = {
  user: null,
  profile: null,
  isLoading: true,
  isPhoneVerified: false,
  isAgeVerified: false,
  hasCountry: false,
  isOnboardingComplete: false,
  error: null
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState(initialState)
  const authStateRef = useRef({ userId: null as string | null, hasLoadedProfile: false })
  const initialSessionProcessedRef = useRef(false)

  const router = useRouter()

  const fetchUserProfile = useCallback(async (): Promise<Profile | null> => {
    try {
      const response = await fetch('/api/profile', {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
      if (!response.ok) {
        logAuthError('profile_fetch', { code: 'profile_fetch_failed', status: response.status })
        return null
      }

      const parsed = authProfileResponseSchema.safeParse(await response.json())
      if (!parsed.success) {
        logAuthError('profile_fetch', { code: 'profile_response_invalid' })
        return null
      }
      return parsed.data.data.profile
    } catch (error) {
      logAuthError('profile_fetch', error)
      return null
    }
  }, [])

  const applyProfile = useCallback((user: User, profile: Profile | null) => {
    if (authStateRef.current.userId !== user.id) return
    if (profile && profile.id !== user.id) {
      logAuthError('profile_fetch', { code: 'profile_identity_mismatch' })
      profile = null
    }
    authStateRef.current = { userId: user.id, hasLoadedProfile: profile !== null }
    setState({
      user,
      profile,
      isLoading: false,
      isPhoneVerified: profile?.phone_verified ?? false,
      isAgeVerified: profile?.age_verified ?? false,
      hasCountry: !!profile?.country,
      isOnboardingComplete: profile?.onboarding_completed ?? false,
      error: null
    })
    setUserContext(user)
  }, [])

  const clearPrivateBrowserStateForUser = useCallback((userId: string) => {
    if (typeof window === 'undefined') return
    clearDashboardSnapshotCacheForUser(window.sessionStorage, userId)
    clearTeamCreationDraftsForUser(window.localStorage, userId)
    clearLegacyTeamCreationDrafts(window.localStorage)
  }, [])

  const refreshProfile = useCallback(async () => {
    if (!state.user) return
    await refreshProfileForIdentity(state.user, {
      fetchProfile: fetchUserProfile,
      getCurrentUserId: () => authStateRef.current.userId,
      applyProfile
    })
  }, [state.user, fetchUserProfile, applyProfile])

  const signOut = useCallback(async () => {
    try {
      const supabase = await createBrowserAuthClient()
      await supabase.auth.signOut()
      setUserContext(null)
      authStateRef.current = { userId: null, hasLoadedProfile: false }
      setState({ ...initialState, isLoading: false })
      // Full navigation so server layouts re-evaluate without a stale session.
      router.replace('/')
      router.refresh()
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Sign out failed'
      }))
    }
  }, [router])

  // Subscribe to auth state changes and keep the client state in sync. Post-auth
  // redirects are NOT performed here — server layouts handle that via
  // `requireVerifiedUser` / `redirectIfAuthenticated`.
  useEffect(() => {
    let disposed = false
    let unsubscribe: (() => void) | null = null

    void createBrowserAuthClient()
      .then((supabase) => {
        if (disposed) return
        const {
          data: { subscription }
        } = supabase.auth.onAuthStateChange((event, session) => {
          const nextUser = session?.user ?? null
          const previousUserId = authStateRef.current.userId
          const decision = resolveAuthTransition({
            event,
            previousUserId,
            nextUserId: nextUser?.id ?? null,
            hasLoadedProfile: authStateRef.current.hasLoadedProfile,
            hasProcessedInitialSession: initialSessionProcessedRef.current
          })

          if (event === 'INITIAL_SESSION') {
            initialSessionProcessedRef.current = true
          }

          if (decision.clearPrivateState && previousUserId) {
            clearPrivateBrowserStateForUser(previousUserId)
          }

          if (decision.recordLogin && nextUser) {
            trackAuthSuccess(nextUser.id)
            trackLogin()
          }

          if (decision.refreshServerTree) {
            router.refresh()
          }

          if (nextUser) {
            authStateRef.current = {
              userId: nextUser.id,
              hasLoadedProfile: decision.preserveProfile
            }
            setState((prev) => {
              return {
                ...prev,
                user: nextUser,
                profile: decision.preserveProfile ? prev.profile : null,
                isLoading: !decision.preserveProfile,
                error: null
              }
            })
          } else {
            setUserContext(null)
            authStateRef.current = { userId: null, hasLoadedProfile: false }
            setState({ ...initialState, isLoading: false })
          }
        })
        unsubscribe = () => subscription.unsubscribe()
        if (disposed) unsubscribe()
      })
      .catch((error) => {
        if (disposed) return
        setState((previous) => ({
          ...previous,
          error:
            error instanceof Error
              ? error.message
              : 'Browser authentication is temporarily unavailable.',
          isLoading: false
        }))
      })

    return () => {
      disposed = true
      if (unsubscribe) unsubscribe()
    }
  }, [clearPrivateBrowserStateForUser, router])

  // Whenever we have a user but no profile loaded, fetch it.
  useEffect(() => {
    if (!state.user || state.profile) return

    let cancelled = false
    ;(async () => {
      const profile = await fetchUserProfile()
      if (cancelled) return
      applyProfile(state.user!, profile)
    })()

    return () => {
      cancelled = true
    }
  }, [state.user, state.profile, fetchUserProfile, applyProfile])

  const value: AuthState = useMemo(
    () => ({ ...state, signOut, refreshProfile }),
    [state, signOut, refreshProfile]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error(
      'useAuth must be used within an AuthProvider. Make sure your component is wrapped with AuthProvider.'
    )
  }
  return context
}
